import React, { useEffect, useMemo, useRef } from "react";
import { Accordion } from "react-bootstrap";
import { Box, ToggleButton, ToggleButtonGroup, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef,
} from "material-react-table";
import { useQuery } from "@tanstack/react-query";
import {
    fetchPnlSnapshotByType,
    PnlSnapshotType,
    PnlSnapshotRow,
} from "./Pnlsummaryview3.actions";
import { NumericCell, NUMERIC_FIELDS, TotalsBar } from "./PnlSummaryView3";

type SnapshotMode = "daily" | "monthly" | "all" | "month_ends";

const monthEndDateStr = (date: Date) => {
    const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return d.toISOString().slice(0, 10);
};

const SNAPSHOT_TYPE_BY_MODE: Record<SnapshotMode, PnlSnapshotType> = {
    all: "all",
    daily: "daily_pnl_change",
    monthly: "monthly_change",
    month_ends: "all_month_end",
};

type PnlSnapshotHistoryTableProps = {
    refreshKey?: string | number;
};

const PnlSnapshotHistoryTable = ({ refreshKey }: PnlSnapshotHistoryTableProps) => {
    const [snapshotMode, setSnapshotMode] = React.useState<SnapshotMode>("daily");
    const [selectedSnapshotDate, setSelectedSnapshotDate] = React.useState<string | null>(null);
    const [selectedMonthAnchor, setSelectedMonthAnchor] = React.useState<Date>(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return d;
    });

    const snapshotTableContainerRef = useRef<HTMLDivElement | null>(null);
    const snapshotTotalsInnerRef = useRef<HTMLDivElement | null>(null);

    const periodParam = useMemo(() => {
        if (snapshotMode === "daily") return selectedSnapshotDate ?? undefined;
        if (snapshotMode === "monthly" || snapshotMode === "month_ends") {
            return monthEndDateStr(selectedMonthAnchor);
        }
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snapshotMode, selectedSnapshotDate, selectedMonthAnchor]);

    const {
        data: snapshotRows = [],
        isLoading: snapshotLoading,
        error: snapshotQueryError,
    } = useQuery<PnlSnapshotRow[], Error>({
        queryKey: ["pnl-snapshot-history", snapshotMode, periodParam, refreshKey],
        queryFn: () => fetchPnlSnapshotByType(SNAPSHOT_TYPE_BY_MODE[snapshotMode], periodParam),
        staleTime: 60_000,
    });

    const snapshotError = snapshotQueryError ? "Failed to load P&L snapshot history" : null;

    useEffect(() => {
        if (snapshotMode !== "daily" || selectedSnapshotDate) return;
        const latestDate = snapshotRows.reduce<string | null>(
            (max, row) => (!max || row.date > max ? row.date : max),
            null
        );
        if (latestDate) setSelectedSnapshotDate(latestDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snapshotMode, snapshotRows]);

    const shiftSelectedDate = (deltaDays: number) => {
        if (!selectedSnapshotDate) return;
        const d = new Date(selectedSnapshotDate);
        d.setDate(d.getDate() + deltaDays);
        setSelectedSnapshotDate(d.toISOString().slice(0, 10));
    };

    const shiftSelectedMonth = (deltaMonths: number) => {
        setSelectedMonthAnchor(prev => new Date(prev.getFullYear(), prev.getMonth() + deltaMonths, 1));
    };

    const filteredSnapshotRows = snapshotRows;

    const snapshotTotals = useMemo<Record<string, number>>(() => {
        return NUMERIC_FIELDS.reduce((acc, field) => {
            acc[field] = filteredSnapshotRows.reduce(
                (sum, row) => sum + (Number((row as any)[field]) || 0),
                0
            );
            return acc;
        }, {} as Record<string, number>);
    }, [filteredSnapshotRows]);

    useEffect(() => {
        if (snapshotTableContainerRef.current && snapshotTotalsInnerRef.current) {
            const scrollLeft = snapshotTableContainerRef.current.scrollLeft;
            snapshotTotalsInnerRef.current.style.transform = `translateX(-${scrollLeft}px)`;
        }
    }, [filteredSnapshotRows]);

    const snapshotColumns = useMemo<MRT_ColumnDef<PnlSnapshotRow>[]>(
        () => [
            { accessorKey: "date", header: "Date", size: snapshotMode === "monthly" ? 190 : 110 },
            { accessorKey: "book", header: "Book", size: 130 },
            { accessorKey: "strategy", header: "Strategy", size: 130 },
            { accessorKey: "pnl", header: "Gross P&L", size: 130, Cell: NumericCell },
            { accessorKey: "daily_change", header: "Gross Daily Change", size: 200, Cell: NumericCell },
            { accessorKey: "markup_pnl", header: "Markup P&L", size: 150, Cell: NumericCell },
            { accessorKey: "optimisation_margin", header: "Optimisation P&L", size: 170, Cell: NumericCell },
            { accessorKey: "commercial_pnl", header: "Commercial P&L", size: 160, Cell: NumericCell },
            { accessorKey: "jv_total_pnl", header: "JV Total P&L", size: 150, Cell: NumericCell },
            { accessorKey: "jv_partner_pnl", header: "JV Partner P&L", size: 160, Cell: NumericCell },
            { accessorKey: "cgm_pnl", header: "CGM P&L", size: 130, Cell: NumericCell },
            { accessorKey: "cost", header: "Costs", size: 130, Cell: NumericCell },
            { accessorKey: "net_pnl", header: "Net P&L", size: 140, Cell: NumericCell },
        ],
        [snapshotMode]
    );

    const monthLabel = selectedMonthAnchor.toLocaleDateString("en-US", { month: "short", year: "numeric" });

    const snapshotTable = useMaterialReactTable({
        columns: snapshotColumns,
        data: filteredSnapshotRows,
        enableColumnFilters: true,
        enableSorting: true,
        enablePagination: true,
        enableColumnResizing: true,
        enableStickyHeader: true,
        enableRowActions: false,
        enableRowSelection: false,
        state: { isLoading: snapshotLoading },
        initialState: { density: "compact", pagination: { pageIndex: 0, pageSize: 50 } },
        layoutMode: "grid-no-grow",
        muiTableContainerProps: {
            ref: snapshotTableContainerRef,
            onScroll: e => {
                const scrollLeft = (e.target as HTMLDivElement).scrollLeft;
                if (snapshotTotalsInnerRef.current) {
                    snapshotTotalsInnerRef.current.style.transform = `translateX(-${scrollLeft}px)`;
                }
            },
            sx: {
                height: "auto",
                maxHeight: "400px",
                overflowX: "auto",
                overflowY: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                backgroundColor: "#ffffff",
            },
        },
        renderTopToolbar: ({ table }) => (
            <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1 }}>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={snapshotMode}
                        onChange={(_, val) => val && setSnapshotMode(val)}
                    >
                        <ToggleButton value="daily" sx={{ textTransform: "none", fontSize: "0.75rem", px: 1.5 }}>
                            Daily
                        </ToggleButton>
                        <ToggleButton value="monthly" sx={{ textTransform: "none", fontSize: "0.75rem", px: 1.5 }}>
                            Monthly
                        </ToggleButton>
                        <ToggleButton value="month_ends" sx={{ textTransform: "none", fontSize: "0.75rem", px: 1.5 }}>
                            Month Ends
                        </ToggleButton>
                        <ToggleButton value="all" sx={{ textTransform: "none", fontSize: "0.75rem", px: 1.5 }}>
                            All
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {snapshotMode === "daily" && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <IconButton size="small" onClick={() => shiftSelectedDate(-1)}>
                                <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                            <span style={{ fontSize: "0.8rem", fontWeight: 600, minWidth: 90, textAlign: "center" }}>
                                {selectedSnapshotDate ?? "—"}
                            </span>
                            <IconButton size="small" onClick={() => shiftSelectedDate(1)}>
                                <ChevronRightIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}

                    {(snapshotMode === "monthly" || snapshotMode === "month_ends") && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <IconButton size="small" onClick={() => shiftSelectedMonth(-1)}>
                                <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                            <span style={{ fontSize: "0.8rem", fontWeight: 600, minWidth: 220, textAlign: "center" }}>
                                {monthLabel}
                                {snapshotRows.length === 0 && !snapshotLoading ? " (no data)" : ""}
                            </span>
                            <IconButton size="small" onClick={() => shiftSelectedMonth(1)}>
                                <ChevronRightIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                </Box>
                <TotalsBar table={table} totals={snapshotTotals} innerRef={snapshotTotalsInnerRef} />
            </Box>
        ),
        muiTableHeadRowProps: {
            sx: {
                background: "white",
                "& .MuiTableCell-root": {
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    color: "#2c3e50",
                    borderRight: "1px solid #dcdcdc",
                },
            },
        },
        muiTableBodyCellProps: {
            sx: {
                fontSize: "0.7rem",
                padding: "4px 8px",
                borderBottom: "1px solid #f1f1f1",
                borderRight: "1px solid #f1f1f1",
            },
        },
    });

    return (
        <Accordion className="table-ad__addInputs" style={{ width: "100%", marginTop: 16 }}>
            <Accordion.Item eventKey="0">
                <Accordion.Header
                    className="accordion_button"
                    style={{
                        fontSize: "0.8rem",
                        backgroundColor: "#d1edf1",
                        padding: "6px 12px",
                        borderRadius: "4px",
                        fontWeight: 600,
                    }}
                >
                    P&L Snapshot History
                </Accordion.Header>
                <Accordion.Body>
                    {snapshotError && (
                        <div style={{ color: "#d32f2f", fontSize: "0.8rem", marginBottom: 8 }}>
                            {snapshotError}
                        </div>
                    )}
                    <div className="table-ad__wrapperCard">
                        <MaterialReactTable table={snapshotTable} />
                    </div>
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
};

export default PnlSnapshotHistoryTable;